/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Field, Fields } from 'redux-form';
import { isAfter } from 'date-fns';

import { classNameWithModifiers as withMods } from 'helpers/class-name';
import { Card, CardTitle } from 'components/Card';
import { DateControls } from 'components/DateControls';
import { DropdownPillMenu } from 'components/Dropdown';
import { Form, Input, Tags } from 'components/Form';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { Title } from 'features/App';

import messages from './Pike13.messages';
import './Pike13.scss';

const Pike13 = ({ isLoading, isSuccess, lastReservationDate, locations, services, pike13OAuthUrl }) => {
  const stepMods = withMods.bind(null, 'pike13__step');
  const subdomainClassName = stepMods({ isDisabled: isSuccess });
  return (
    <div className="pike13">
      <Message message={messages.title} as={Title} />

      <Form>
        <Card className={subdomainClassName}>
          <CardTitle>
            <div>
              <Message className="pike13__step-number" value={1} format="decimal" />
              <Message className="pike13__step-title" message={messages.subdomainTitle} />
            </div>
            {isLoading.pike13Data && <LoadingDots className="pike13__loading-dots" />}
          </CardTitle>
          {!isSuccess && (
            <>
              <Message as="p" className="text--light" message={messages.subdomainDesc} />
              <div className="pike13__subdomain-input">
                <span className="pike13__subdomain-input-static">https://</span>
                <Field type="text" name="subdomain" className="mb-0" component={Input} />
                <span className="pike13__subdomain-input-static">.pike13.com</span>
              </div>
            </>
          )}
        </Card>

        <Fields
          names={['venueId', 'subdomain', 'transitionDate']}
          component={props => {
            const {
              venueId: {
                input: { value: venueId },
              },
              subdomain: {
                input: { value: subdomain },
              },
              transitionDate: { input },
            } = props;
            const isDisabled = !venueId || !subdomain || !lastReservationDate || isSuccess;
            const transitionDateClassName = stepMods({ isDisabled });

            return (
              <Card className={transitionDateClassName}>
                <CardTitle>
                  <div>
                    <Message className="pike13__step-number" value={2} format="decimal" />
                    <Message className="pike13__step-title" message={messages.transitionDateTitle} />
                  </div>
                  {isLoading.lastReservationDate && <LoadingDots className="pike13__loading-dots" />}
                </CardTitle>
                {!isDisabled && (
                  <>
                    <Message as="p" className="text--light" message={messages.transitionDateDesc1} />
                    <Message as="p" className="text--light" message={messages.transitionDateDesc2} />
                    <DateControls
                      className="pike13__date-picker"
                      date={input.value}
                      setDate={input.onChange}
                      attachment="right"
                      isValidDate={current => isAfter(current.toDate(), lastReservationDate)}
                    />
                  </>
                )}
              </Card>
            );
          }}
        />

        <Fields
          names={['subdomain', 'locationId']}
          component={props => {
            const {
              subdomain: {
                input: { value: subdomain },
              },
              locationId: {
                input: { value, onChange },
              },
            } = props;
            const isDisabled = !subdomain || !locations.length || isSuccess;
            const locationClassName = stepMods({ isDisabled });

            return (
              <Card className={locationClassName}>
                <CardTitle>
                  <div>
                    <Message className="pike13__step-number" value={3} format="decimal" />
                    <Message className="pike13__step-title" message={messages.locationIdTitle} />
                  </div>
                  {isLoading.pike13Data && <LoadingDots className="pike13__loading-dots" />}
                </CardTitle>
                {!isDisabled && (
                  <>
                    <Message as="p" className="text--light" message={messages.locationIdDesc} />
                    <DropdownPillMenu
                      attachment="left"
                      items={locations}
                      getKey="id"
                      getText="label"
                      isSelected={({ id }) => id === value}
                      onSelect={({ id }) => onChange(id)}
                      isWide
                      renderAsActive={false}
                    />
                  </>
                )}
              </Card>
            );
          }}
        />

        <Fields
          names={['subdomain', 'classIds']}
          component={props => {
            const {
              subdomain: {
                input: { value: subdomain },
              },
            } = props;
            const isDisabled = !subdomain || !locations.length || isSuccess;
            const classIdsClassName = stepMods({ isDisabled });

            return (
              <Card className={classIdsClassName}>
                <CardTitle>
                  <div>
                    <Message className="pike13__step-number" value={4} format="decimal" />
                    <Message className="pile13__step-title" message={messages.classIdsTitle} />
                  </div>
                  {isLoading.pike13Data && <LoadingDots />}
                </CardTitle>
                {!isDisabled && (
                  <>
                    <Message as="p" className="text--light" message={messages.classIdsDesc1} />
                    <Message as="p" className="text--light" message={messages.classIdsDesc2} />
                    <Field
                      name="classIds"
                      tagTree={services}
                      format={value => value.reduce((acc, x) => ({ ...acc, [x]: true }), {})}
                      parse={value => Object.entries(value).reduce((acc, [k, v]) => (v ? [...acc, k] : acc), [])}
                      component={Tags}
                    />
                  </>
                )}
              </Card>
            );
          }}
        />

        <Fields
          names={['subdomain', 'classIds']}
          component={props => {
            const {
              subdomain: {
                input: { value: subdomain },
              },
              classIds: {
                input: { value: classIds },
              },
            } = props;
            const isDisabled = !subdomain || !classIds.length;
            const authorizeClassName = stepMods({ isDisabled });

            return (
              <Card className={authorizeClassName}>
                <CardTitle>
                  <div>
                    <Message className="pike13__step-number" value={5} format="decimal" />
                    <Message className="pile13__step-title" message={messages.authorizeTitle} />
                  </div>
                </CardTitle>
                {!isDisabled && (
                  <>
                    {isLoading.migration && <LoadingDots />}
                    {!isLoading.migration && !isSuccess && (
                      <>
                        <Message as="p" className="text--light" message={messages.authorizeDesc} />
                        <p>
                          <Message
                            as="a"
                            className="bt bt--primary"
                            href={pike13OAuthUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...messages.authorizeButton}
                          />
                        </p>
                      </>
                    )}
                    {!isLoading.migration && isSuccess && (
                      <Message as="p" className="text--light" message={messages.integrationSuccess} />
                    )}
                  </>
                )}
              </Card>
            );
          }}
        />
      </Form>
    </div>
  );
};

Pike13.propTypes = {
  isLoading: PropTypes.shape({
    lastReservationDate: PropTypes.bool,
    pike13Data: PropTypes.bool,
    migration: PropTypes.bool,
  }),
  isSuccess: PropTypes.bool,
  lastReservationDate: PropTypes.instanceOf(Date),
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
    })
  ),
  services: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  pike13OAuthUrl: PropTypes.string,
};

export default Pike13;
